/* eslint-disable class-methods-use-this */
/* eslint-disable @typescript-eslint/no-non-null-assertion, max-len */

import { catchError, take, tap } from 'rxjs/operators';

import { Component, DestroyRef, ElementRef, inject, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Meta } from '@angular/platform-browser';

import { S2ANotificationService } from '@s2a-core/ng-core';
import { Logger } from '@s2a-core/ui-logger';
import { EquipmentQueryService, EquipmentService, StoredEquipment } from '@s2a-equipment/core';
import { AuthService, LOCALSTORAGE_USER_SESSION, ShellEvent, Translation, User, UserWithRights } from '@s2a/core';

import { ComponentLoaderService, ComponentMap, SessionService } from '~ng-shell';

import { environment } from '../environments/environment';
import { UserPassswordResetStatus } from './enum/user.enum';
import { WebComponentText } from './models/web-component-text.model';
import { HeartBeatService } from './services/heartbeat.service';
import { PageLoaderService } from './services/pageLoader.service';
import { RouterService } from './services/router.service';
import { ShellAuthenticationService } from './services/shell-authentication.service';
import { SupportRequestService } from './services/support-request.service';
import { ExternalSystemStatusService } from './services/external-system-status.service';
import {
  ExternalSystemCurrentAndPreviousStateStatus,
  ExternalSystemStatusItem,
  ExternalSystemStatusType,
} from './models/external-system.status.model';
import { ExternalSystemStatusDialogueComponent } from './components/external-system-status-dialogue/external-system-status-dialogue-component';
import {
  AND,
  AVAILABLE_AGAIN,
  AVAILABLE_AGAIN_MULTI,
  CONNECTION_EDGE_DEVICE_RESTORED,
  CONTACT_SUPPORT,
  LINE,
  LINES_CURRENTLY_UNAVAILABLE,
  NO_CONECTION_TO_EDGE_DEVICE,
} from './constants/app.constant';

const logger = Logger('App Shell');

@Component({
  selector: 's2a-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit {
  @ViewChild('authPlace', { static: true }) authPlace: ElementRef;
  @ViewChild('changePwdDialog') changePwdDialog: TemplateRef<any>;

  isNavigationMenuVisible = true;
  isAuthenticated = false;
  appBellOpened = false;
  languageRecord: WebComponentText = {};
  suppotRequestDialogueRecord: WebComponentText = {};
  changePwdDialogRef: MatDialogRef<any>;
  lines: StoredEquipment[] = [];
  externalStatusBannerItems: ExternalSystemStatusItem[] = [];
  masterExternalStatusItems: ExternalSystemStatusItem[] = [];
  bannerItemsState = new Map<string, ExternalSystemStatusItem>();
  successStatusBannerItem = {
    type: ExternalSystemStatusType.success,
    accountId: '',
    title: CONNECTION_EDGE_DEVICE_RESTORED,
    subTitle: '',
    description: '',
    currentState: {
      status: ExternalSystemCurrentAndPreviousStateStatus.connected,
      timestamp: '',
    },
    previousState: {
      status: ExternalSystemCurrentAndPreviousStateStatus.connected,
      timestamp: '',
    },
    metaData: {
      lineId: '',
      lineName: '',
    },
    children: [],
  };
  errorStatusBannerItem: ExternalSystemStatusItem = {
    type: ExternalSystemStatusType.error,
    accountId: '',
    title: '',
    subTitle: '',
    description: CONTACT_SUPPORT,
    currentState: {
      status: ExternalSystemCurrentAndPreviousStateStatus.disconnected,
      timestamp: '',
    },
    previousState: {
      status: ExternalSystemCurrentAndPreviousStateStatus.disconnected,
      timestamp: '',
    },
    metaData: {
      lineId: '',
      lineName: '',
    },
    children: [],
  };

  private _destroyRef: DestroyRef;

  constructor(
    public pageLoaderService: PageLoaderService,
    public translateService: Translation,
    public shellAuthenticationService: ShellAuthenticationService,
    private authService: AuthService,
    private heartBeatService: HeartBeatService,
    private sessionService: SessionService,
    private routerService: RouterService,
    private meta: Meta,
    private dialog: MatDialog,
    private notificationService: S2ANotificationService,
    private componentLoaderService: ComponentLoaderService,
    private equipmentService: EquipmentService,
    private equipmentQueryService: EquipmentQueryService,
    private supportRequestService: SupportRequestService,
    private externalSystemStatusService: ExternalSystemStatusService,
  ) {
    this._destroyRef = inject(DestroyRef);
  }

  ngOnInit(): void {
    this.getLineEquipments();
    this.subscribeToLanguageChange();
    this.languageRecord = Object.assign({}, this.getTranslatedText());
    this.suppotRequestDialogueRecord = Object.assign({}, this.getSupportRequestTranslatedText());
    this.getUserInfo();
    // Disable indexing non-production environment
    if (!environment.production) {
      this.meta.addTags([
        // To prevent most search engines from indexing a page on your site
        { name: 'robots', content: 'noindex' },
        // To prevent only Google web crawlers from indexing a page
        { name: 'googlebot', content: 'noindex' },
      ]);
    }
    this.authService.isLoggedIn$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap((isLogin: boolean) => {
          this.isAuthenticated = isLogin;
          if (!isLogin) {
            this.sessionService.clearSession();
            this.routerService.redirectToLogin();
          }
        }),
      )
      .subscribe();

    this.shellAuthenticationService
      .refreshRights()
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap(() => {
          if (!this.isAuthenticated) {
            this.authService.isLoggedIn = true;
          }
          this.isAuthenticated = true;
        }),
        catchError(() => {
          this.authService.isLoggedIn = false;
          this.isAuthenticated = false;

          return 'Failed to refresh user';
        }),
      )
      .subscribe();

    this.authService.hasGlobalRights$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((globalRights) => {
      let user = this.authService.user!;
      user = { ...user, globalRights };
      this.sessionService.setUserInformation(user);
    });

    this.heartBeatService.heartBeat().pipe(takeUntilDestroyed(this._destroyRef)).subscribe();

    const userSession = sessionStorage.getItem(LOCALSTORAGE_USER_SESSION);
    if (!userSession || Object.keys(userSession).length === 0) {
      this.authService.isLoggedIn = false;
      this.isAuthenticated = false;
    }

    let previousUrl = '';
    window.addEventListener(
      'message',
      (event) => {
        if (event.data.type === ShellEvent.RouteChange) {
          if (event.data.value !== previousUrl) {
            this.routerService.changeRoute(event);
            previousUrl = event.data.value;
          }
        } else if (event.data.type === ShellEvent.UserChange) {
          this.shellAuthenticationService
            .fetchUserWithRights(this.authService.user!.userId)
            .pipe(
              takeUntilDestroyed(this._destroyRef),
              catchError(() => {
                this.authService.isLoggedIn = false;
                this.isAuthenticated = false;

                return 'Failed to fetch user with rights';
              }),
            )
            .subscribe();
        } else if (event.data.type === ShellEvent.PrintModeChange) {
          this.isNavigationMenuVisible = event.data.value;
        }
      },
      false,
    );

    this.componentLoaderService.loadComponent([ComponentMap.ChangePassword]);
    this.subscribeToExternalDeviceStatus();
  }
  /**
   * The function is used to subscribe to language change observable
   */
  subscribeToLanguageChange(): void {
    this.translateService.onLangChange.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      this.languageRecord = Object.assign({}, this.getTranslatedText());
      this.suppotRequestDialogueRecord = Object.assign({}, this.getSupportRequestTranslatedText());
    });
  }

  /**
   * The function is used to toggle app bell side bar
   */
  toggleAppBellHandler(forciblyHide: boolean): void {
    this.appBellOpened = forciblyHide ? !forciblyHide : !this.appBellOpened;
  }

  /**
   * Method is used to open the external system detail dialogue
   */
  openExternalSystemDetailDialogue(item: ExternalSystemStatusItem): void {
    const dialogeRef = this.dialog.open(ExternalSystemStatusDialogueComponent, {
      data: item,
      disableClose: true,
      width: '480px',
    });
    dialogeRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((data: boolean) => {
        if (data) {
          this.redirectToContactSupport(item);
        }
      });
  }

  /**
   * Method is used to redirect to contact support UI
   */
  redirectToContactSupport(item: ExternalSystemStatusItem): void {
    this.routerService.redirectToApp(`/support?mode=create&lineId=${item.metaData.lineId}`);
  }

  /**
   * Method is used to remove the banner
   */
  removeBannerItem(_item: ExternalSystemStatusItem): void {
    this.externalStatusBannerItems.shift();
    this.masterExternalStatusItems.forEach((item: ExternalSystemStatusItem) => {
      this.bannerItemsState.delete(item.metaData.lineId);
    });
    this.masterExternalStatusItems = [];
  }
  /**
   * The function is used to show notification on password updation api response
   *
   * @param event CustomEvent
   */
  onPasswordChangedHandler(event: CustomEvent): void {
    const status = event.detail.status;
    if (status) {
      this.changePwdDialogRef.close();
      this.notificationService.success(this.translateService.instant('components.users.change_password.success'));
    } else {
      this.notificationService.error(this.translateService.instant('components.users.change_password.failure'));
    }
  }

  /**
   * The function is used to get the translated text based on translation key
   *
   * @returns WebComponentText
   */
  getTranslatedText(): { [key: string]: string } {
    const languageText: WebComponentText = {};

    languageText.title = this.translateService.instant('components.users.change_password.forced_title');
    languageText.currentPwdText = this.translateService.instant('components.users.change_password.current_password');
    languageText.passwordIsRequiredText = this.translateService.instant(
      'components.users.change_password.password_is_required',
    );
    languageText.passwordMinLengthText = this.translateService.instant(
      'components.users.change_password.password_minlength',
    );
    languageText.passwordMaxLengthText = this.translateService.instant(
      'components.users.change_password.password_maxlength',
    );
    languageText.passwordText = this.translateService.instant('components.users.change_password.password');
    languageText.passwordHintText = this.translateService.instant(
      'components.users.edit_user.user_data.user_infos.password_hint',
    );

    languageText.passwordPatternText = this.translateService.instant(
      'components.users.change_password.password_pattern',
    );

    languageText.passwordConfirmationText = this.translateService.instant(
      'components.users.change_password.password_confirmation',
    );

    languageText.passwordConfirmationIsRequiredText = this.translateService.instant(
      'components.users.change_password.password_confirmation_is_required',
    );

    languageText.paswordConfirmtationMismatchText = this.translateService.instant(
      'components.users.change_password.password_confirmation_mismatch',
    );

    languageText.cancelText = this.translateService.instant('global.cancel');
    languageText.saveText = this.translateService.instant('global.save');

    return languageText;
  }

  /**
   * The method is used to return the translated text based on the language key fo rsupport request dialogue
   *
   * @returns WebComponentText
   */
  getSupportRequestTranslatedText(): { [key: string]: string } {
    const languageText: WebComponentText = {};

    languageText.title = this.translateService.instant('components.customer_support.title');
    languageText.name = this.translateService.instant('components.customer_support.name');
    languageText.email = this.translateService.instant('components.customer_support.email');
    languageText.company = this.translateService.instant('components.customer_support.company');
    languageText.phoneNumber = this.translateService.instant('components.customer_support.phoneNumber');
    languageText.affectedLine = this.translateService.instant('components.customer_support.affectedLine');

    languageText.subject = this.translateService.instant('components.customer_support.subject');

    languageText.description = this.translateService.instant('components.customer_support.description');

    languageText.service = this.translateService.instant('components.customer_support.service');

    languageText.save = this.translateService.instant('components.customer_support.send');
    languageText.requiredField = this.translateService.instant('components.customer_support.field_is_required');
    languageText.emailInvalid = this.translateService.instant('components.customer_support.email_invalid');

    languageText.email_or_phoneprompt = this.translateService.instant(
      'components.customer_support.email_or_phoneprompt',
    );
    languageText.or = this.translateService.instant('components.customer_support.or');

    return languageText;
  }

  /**
   * The function is used to invoke backend to create support ticket in sales force
   *
   * @param payload An object containing the key value pair
   */
  public onCreateTicketClicked(payload: { [key: string]: string }): void {
    this.supportRequestService.createSupportRequestTicket(payload).subscribe({
      next: () => {
        this.notificationService.success(
          this.translateService.instant('components.customer_support.support_ticket_success'),
        );
      },
      error: (error: any) => {
        logger.error(JSON.stringify(error.error));
        this.notificationService.error(
          this.translateService.instant('components.customer_support.support_ticket_failure'),
        );
      },
    });
  }

  /**
   * The function is used to get the logged in user information from API
   */
  private getUserInfo(): void {
    const userId = (this.authService.user || {}).userId as string;
    this.shellAuthenticationService
      .fetchUserInfo(userId)
      .pipe(takeUntilDestroyed(this._destroyRef), take(1))
      .subscribe((response: User) => {
        if (response.confirmation && response.confirmation.status === UserPassswordResetStatus.FORCED_PASSWORD_RESET) {
          this.sessionService.setUserInformation(response);
          this.showChangePasswordDialogue(this.authService.user!);
        }
      });
  }

  /**
   * The function is used to show the change password dialogue
   *
   * @param user UserWithRights
   */
  private showChangePasswordDialogue(user: UserWithRights): void {
    this.changePwdDialogRef = this.dialog.open(this.changePwdDialog, {
      width: '360px',
      disableClose: true,
      data: {
        user,
      },
    });
  }

  /**
   * Method is used to get the line equipments.
   *
   */
  private getLineEquipments(): void {
    this.equipmentService.ensureCache().pipe(takeUntilDestroyed(this._destroyRef)).subscribe();
    this.equipmentQueryService
      .selectFilter('description', 'line')
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (equipments: StoredEquipment[]) => {
          this.lines = equipments;
        },
        error: () => {
          this.lines = [];
        },
      });
  }

  /**
   * Method is used to subscribe to external device status and show notification in the form of banner
   */
  private subscribeToExternalDeviceStatus(): void {
    this.externalSystemStatusService.externalSystemStatus$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((linesInformation: ExternalSystemStatusItem | ExternalSystemStatusItem[] | null) => {
        if (linesInformation) {
          const arr: ExternalSystemStatusItem[] = [];
          let graphlQlItems = arr.concat(linesInformation);
          graphlQlItems.forEach((item: ExternalSystemStatusItem) => {
            if (item.type === ExternalSystemStatusType.success) {
              // Look for the item if we have unsuccessful item before or not
              const masterItem = this.bannerItemsState.get(item.metaData.lineId);
              if (masterItem?.type === ExternalSystemStatusType.error) {
                this.bannerItemsState.set(item.metaData.lineId, item);
              }
            } else {
              this.bannerItemsState.set(item.metaData.lineId, item);
            }
          });
          graphlQlItems = Array.from(this.bannerItemsState.values());
          this.handleSuccessfulLines(graphlQlItems);
          this.handleFailureLines(graphlQlItems);
        }
      });
  }

  /**
   * Method is used to create failure banner item
   *
   * @param graphlQlItems ExternalSystemStatusItem[]
   */
  private handleFailureLines(graphlQlItems: ExternalSystemStatusItem[]): void {
    let failureItems = graphlQlItems.filter((item) => item.type === ExternalSystemStatusType.error);
    const errorBannerItem = this.externalStatusBannerItems.find(
      (bannerItem) => bannerItem.type === ExternalSystemStatusType.error,
    );
    let title = '';
    let subTitle = '';

    if (failureItems.length) {
      // convert sub title and title of each items
      failureItems = failureItems.map((item) => ({
        ...item,
        ...{ title: item.metaData.lineName, subTitle: `${NO_CONECTION_TO_EDGE_DEVICE}.` },
      }));
      // check if we the same line id exists in the UI or not. If it exists..then update timestamp
      if (errorBannerItem) {
        const errorItems = errorBannerItem.children?.length ? errorBannerItem.children : [errorBannerItem];

        failureItems = failureItems.reduce((accumulator: any, failureItem: ExternalSystemStatusItem) => {
          const errorItem = errorItems.find((item) => item.metaData.lineId === failureItem.metaData.lineId);
          if (!errorItem) {
            accumulator.push(failureItem);

            return accumulator;
          }
        }, []);
      }

      failureItems = this.sortDisconectedItems(failureItems);

      if (failureItems?.length) {
        if (failureItems?.length > 1) {
          this.errorStatusBannerItem.children = this.errorStatusBannerItem.children?.concat(failureItems);
          title = `${NO_CONECTION_TO_EDGE_DEVICE}: ${failureItems.length} ${LINE}s`;
          subTitle = this.constructMessages(failureItems);
          this.updateDisconnectedStateBannerItemMessages(title, subTitle);
          this.externalStatusBannerItems.push(this.errorStatusBannerItem);
        } else if (errorBannerItem && failureItems) {
          if (errorBannerItem.children?.length) {
            const children = errorBannerItem.children?.concat(failureItems);
            errorBannerItem.children = this.sortDisconectedItems(children);
          } else {
            const existingItem = JSON.parse(JSON.stringify(errorBannerItem));
            existingItem.title = existingItem.metaData.lineName;
            existingItem.subTitle = `${NO_CONECTION_TO_EDGE_DEVICE}.`;
            errorBannerItem.children = [];
            errorBannerItem.children = this.sortDisconectedItems(
              errorBannerItem.children?.concat(existingItem, failureItems),
            );
          }
          title = `${NO_CONECTION_TO_EDGE_DEVICE}: ${errorBannerItem.children?.length} ${LINE}s`;
          subTitle = this.constructMessages(errorBannerItem.children);
          this.updateDisconnectedStateBannerItemMessages(title, subTitle);
        } else if (failureItems.length === 1) {
          this.errorStatusBannerItem = failureItems[0];
          subTitle = `${this.errorStatusBannerItem.metaData.lineName} ${LINES_CURRENTLY_UNAVAILABLE}`;
          this.updateDisconnectedStateBannerItemMessages(`${NO_CONECTION_TO_EDGE_DEVICE}:`, subTitle);
          this.externalStatusBannerItems.push(this.errorStatusBannerItem);
        }
        this.removeSuccessfulItemsFromMasterCollectionIfExists();
      }
    }
  }

  /**
   * Method is used to sort items
   *
   * @param failureItems ExternalSystemStatusItem[]
   */
  private sortDisconectedItems(failureItems: ExternalSystemStatusItem[]): ExternalSystemStatusItem[] {
    // sort the collection
    return (failureItems = (failureItems ?? []).sort(
      (itemA: ExternalSystemStatusItem, itemB: ExternalSystemStatusItem) => {
        if (itemA && itemB) {
          return itemA.currentState.timestamp < itemB.currentState.timestamp ? 1 : -1;
        } else {
          return 0;
        }
      },
    ));
  }

  /**
   * Method is used to create successful banner item that calls internal methods to create master records
   *
   * @param graphlQlItems ExternalSystemStatusItem[]
   */
  private handleSuccessfulLines(graphlQlItems: ExternalSystemStatusItem[]): void {
    this.updateMasterRecords(graphlQlItems);
    // Create the successful messages and update the item
    const connectedStatusItemDescription = this.constructConnectedStateItemDescription();
    this.successStatusBannerItem.subTitle = `${this.masterExternalStatusItems.length} ${this.masterExternalStatusItems.length > 1 ? `${LINE}s` : `${LINE}`}:`;
    this.successStatusBannerItem.description = connectedStatusItemDescription;
    // push the item onto the banner array if not exists
    const successfulBannerItem = this.externalStatusBannerItems.find(
      (externalStatusBannerItem) => externalStatusBannerItem.type === ExternalSystemStatusType.success,
    );
    if (!successfulBannerItem && this.masterExternalStatusItems.length) {
      // insert sucessful records always at the top
      this.externalStatusBannerItems.unshift(this.successStatusBannerItem);
    }

    this.removeFailedItemsFromDisconnectedCollectionIfExists();
  }

  /**
   * The method is used to create master records for successful connected lines.
   *
   * @param graphlQlItems ExternalSystemStatusItem[]
   */
  private updateMasterRecords(graphlQlItems: ExternalSystemStatusItem[]): void {
    // filter out the successful and failure items
    const successfulItems = graphlQlItems.filter(
      (successFulItem) => successFulItem.type === ExternalSystemStatusType.success,
    );
    // update the master records for successful lines
    successfulItems.forEach((successfulItem) => {
      const masterStatusItemIndex = this.masterExternalStatusItems.findIndex(
        (masterItem) => masterItem.metaData.lineId === successfulItem.metaData.lineId,
      );
      if (masterStatusItemIndex !== -1) {
        this.masterExternalStatusItems.splice(masterStatusItemIndex, 1, successfulItem);
      } else {
        this.masterExternalStatusItems.push(successfulItem);
      }
    });
  }

  /**
   * The function removes the disconnected items from disconnected banner items children if that line
   * exist in the master list and when the disconnected banner items children length becomes zero, then
   * remove diconnected banner item from banner lists also
   */
  private removeFailedItemsFromDisconnectedCollectionIfExists(): void {
    // remove the successful items from the disconnected array if it exists
    const disconnectedItem = this.externalStatusBannerItems.find(
      (item) => item.type === ExternalSystemStatusType.error,
    );
    if (disconnectedItem) {
      const children = disconnectedItem.children?.length ? disconnectedItem.children : [disconnectedItem];
      this.masterExternalStatusItems.forEach((masterItem) => {
        const index = children.findIndex((childItem) => childItem.metaData.lineId === masterItem.metaData.lineId);
        if (index !== -1) {
          children?.splice(index, 1);
          const title = `${NO_CONECTION_TO_EDGE_DEVICE}: ${children?.length} ${LINE}s`;
          const subTitle = this.constructMessages(children);
          this.updateDisconnectedStateBannerItemMessages(title, subTitle);
        }
        if (children.length === 1 || !children.length) {
          this.removeDisconnectedBannerItem();
          if (children.length === 1) {
            this.errorStatusBannerItem = children[0];
            const subTitle = `${this.errorStatusBannerItem.metaData.lineName} ${LINES_CURRENTLY_UNAVAILABLE}`;
            this.updateDisconnectedStateBannerItemMessages(`${NO_CONECTION_TO_EDGE_DEVICE}:`, subTitle);
            this.externalStatusBannerItems.push(this.errorStatusBannerItem);
          }
        }
      });
    }
  }

  /**
   * Method is used to update external system status banner item messages
   *
   * @param item ExternalSystemStatusItem
   */
  private updateDisconnectedStateBannerItemMessages(title: string, subTitle: string): void {
    this.errorStatusBannerItem.title = title;
    this.errorStatusBannerItem.subTitle = subTitle;
    this.errorStatusBannerItem.description = CONTACT_SUPPORT;
  }

  /**
   * Method is used to remove disconncted state banner item
   */
  private removeDisconnectedBannerItem(): void {
    // remove the unsuccessful items from the banner list also
    const unSuccessfulItemIndex = this.externalStatusBannerItems.findIndex(
      (unSuccessFulItem) => unSuccessFulItem.type === ExternalSystemStatusType.error,
    );
    if (unSuccessfulItemIndex !== -1) {
      this.externalStatusBannerItems.splice(unSuccessfulItemIndex, 1);
    }
  }

  /**
   * The function removes the successful items from master collection if that line
   * exist in the disconnected list and when the master collection length becomes zero, then
   * remove successful banner item from banner lists also
   */
  private removeSuccessfulItemsFromMasterCollectionIfExists(): void {
    // remove the failed items from successful records if it exists
    const items = this.errorStatusBannerItem.children?.length
      ? this.errorStatusBannerItem.children
      : [this.errorStatusBannerItem];
    items.forEach((item) => {
      const index = this.masterExternalStatusItems?.findIndex(
        (masterSuccessfulItem) => masterSuccessfulItem.metaData.lineId === item.metaData.lineId,
      );

      if (index !== -1) {
        this.masterExternalStatusItems.splice(index, 1);
      }
    });
    if (this.masterExternalStatusItems.length) {
      const connectedStatusItemDescription = this.constructConnectedStateItemDescription();
      this.successStatusBannerItem.subTitle = `${this.masterExternalStatusItems.length} ${this.masterExternalStatusItems.length > 1 ? `${LINE}s` : `${LINE}`}:`;
      this.successStatusBannerItem.description = connectedStatusItemDescription;
    } else {
      // remove the successful items from the banner list also
      const successfulItemIndex = this.externalStatusBannerItems.findIndex(
        (successFulItem) => successFulItem.type === ExternalSystemStatusType.success,
      );
      if (successfulItemIndex !== -1) {
        this.externalStatusBannerItems.splice(successfulItemIndex, 1);
      }
    }
  }

  private constructMessages(items: ExternalSystemStatusItem[]): string {
    let description = '';
    if (items.length) {
      const failedStatusItems = items.reduce((accumulator: string[], item: ExternalSystemStatusItem) => {
        accumulator.push(item.metaData.lineName);

        return accumulator;
      }, []);

      const verb = `${LINE}s ${LINES_CURRENTLY_UNAVAILABLE}`;
      description = `${failedStatusItems.slice(0, -1).join(', ')}${
        failedStatusItems.length > 1 ? ` ${AND} ${failedStatusItems.at(-1)}` : ''
      } ${verb}`.trim();
    }

    return description;
  }

  /**
   * Method is used to create description for successful banner item
   *
   * @returns string
   */
  private constructConnectedStateItemDescription(): string {
    let description = '';
    if (this.masterExternalStatusItems.length) {
      const successStatusItems = this.masterExternalStatusItems.reduce(
        (accumulator: string[], item: ExternalSystemStatusItem) => {
          accumulator.push(item.metaData.lineName);

          return accumulator;
        },
        [],
      );

      const verb = successStatusItems.length > 1 ? AVAILABLE_AGAIN_MULTI : AVAILABLE_AGAIN;

      if (successStatusItems.length === 1) {
        description = `${successStatusItems[0]} ${verb}`;
      } else {
        description = `${successStatusItems.slice(0, -1).join(', ')}${
          successStatusItems.length > 1 ? ` ${AND} ${successStatusItems.at(-1)}` : ''
        } ${verb}`.trim();
      }
    }

    return description;
  }
}
